import { Box, Button, Typography, useMediaQuery, useTheme, Container, Stack } from '@mui/material';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade } from 'swiper/modules';
import { Divider } from '@mui/material';
import UnderConstruction from 'pages/Maintenance/UnderConstruction';
import LexisLogo from '../../assets/images/intro/LEXIS_The_Platform_RGB_transparent.png';
import EFP_logo from '../../assets/images/intro/EFP_logo.png';
import KarolinaHPC from '../../assets/images/intro/KarolinaHPC.jpg';
import LumiHPC from '../../assets/images/intro/LumiHPC.jpg';
import RuthPraha from '../../assets/images/intro/RuthPraha.png';
import RuthBosten60k from '../../assets/images/intro/RuthBosten60k.jpeg';
import Molecule from '../../assets/images/intro/Molecule.jpg';
import executionProgressDark from '../../assets/images/intro/executionProgressDark.png';
import executionProgressLight from '../../assets/images/intro/executionProgressLight.png';
import {
    IS_EFP,
    DOCUMENTATION_URL,
    IS_UNDER_CONSTRUCTION,
    GIT_COMMIT_HASH,
    GIT_BRANCH_NAME,
    WEBSITE_URL,
} from 'config';
import { getOAuth2StartUrl } from 'utils/oauth';

const isEFP = IS_EFP;
const documentationUrl = DOCUMENTATION_URL;

const LoginPage = () => {
    const isUnderConstruction = IS_UNDER_CONSTRUCTION;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const isDarkMode = theme.palette.mode === 'dark';

    const backgroundImages = [
        { src: KarolinaHPC, alt: 'Karolina HPC' },
        { src: LumiHPC, alt: 'Lumi HPC' },
        {
            src: isDarkMode ? executionProgressDark : executionProgressLight,
            alt: 'Execution Progress',
        },
        { src: RuthBosten60k, alt: 'Ruth Bosten 60k' },
        { src: Molecule, alt: 'Molecule' },
        { src: RuthPraha, alt: 'Ruth Praha' },
    ];

    return isUnderConstruction ? (
        <UnderConstruction />
    ) : (
        <Box
            sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                minHeight: '100vh',
                overflow: 'hidden',
                overflowY: 'hidden',
                overflowX: 'hidden',
            }}>
            <Box
                sx={{
                    width: isMobile ? '100%' : '70%',
                    height: isMobile ? '40vh' : '100vh',
                    position: 'relative',
                    overflow: 'hidden',
                    overflowY: 'hidden',
                    overflowX: 'hidden',
                }}>
                <Swiper
                    modules={[Autoplay, EffectFade]}
                    effect='fade'
                    fadeEffect={{ crossFade: true }}
                    autoplay={{
                        delay: 4000,
                        disableOnInteraction: false,
                    }}
                    loop={true}
                    speed={1000}
                    style={{
                        width: '100%',
                        height: '100%',
                    }}>
                    {backgroundImages.map((image, index) => (
                        <SwiperSlide key={index}>
                            <Box
                                sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    backgroundColor: 'background.paper',
                                }}>
                                <Box
                                    component='img'
                                    src={image.src}
                                    alt={image.alt}
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        objectPosition: 'center',
                                        display: 'block',
                                    }}
                                />
                            </Box>
                        </SwiperSlide>
                    ))}
                </Swiper>
            </Box>

            <Box
                sx={{
                    width: isMobile ? '100%' : '30%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    bgcolor: 'background.default',
                    p: { xs: 2, sm: 4 },
                    position: 'relative',
                    pb: { xs: '100px', sm: '90px' },
                    pt: { xs: '50px', sm: '20px' },
                }}>
                <Container maxWidth='xs'>
                    <Stack spacing={5} alignItems='center' sx={{ width: '100%', mb: { xs: 5, sm: 5 } }}>
                        <Box
                            component='img'
                            src={isEFP ? EFP_logo : LexisLogo}
                            alt={isEFP ? 'EFP Logo' : 'LEXIS2 Platform Logo'}
                            sx={{
                                maxWidth: '500px',
                                width: '100%',
                                height: 'auto',
                                objectFit: 'contain',
                                mb: { xs: 3, sm: 5 },
                                transform: 'scale(1.2)',
                                transition: 'transform 0.3s ease',
                            }}
                        />
                        <Link reloadDocument={true} to={getOAuth2StartUrl()} style={{ width: '80%' }}>
                            <Button
                                fullWidth
                                variant='outlined'
                                color='primary'
                                sx={{
                                    py: { xs: 1.2, sm: 1.5 },
                                    fontSize: { xs: '1rem', sm: '1.1rem' },
                                    borderRadius: 2,
                                }}>
                                Login
                            </Button>
                        </Link>
                    </Stack>
                </Container>

                <Container maxWidth='xs'>
                    <Divider
                        sx={{
                            my: 2,
                            borderColor: 'grey.300',
                            zIndex: 2000,
                            color: 'grey.300',
                            mb: { xs: 2, sm: 3 },
                        }}>
                        Other Links
                    </Divider>
                    <Stack spacing={2} alignItems='center' sx={{ width: '100%' }}>
                        <Button
                            component='a'
                            href={WEBSITE_URL}
                            target='_blank'
                            rel='noopener noreferrer'
                            variant='contained'
                            fullWidth
                            sx={{
                                py: { xs: 1.2, sm: 1.5 },
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                borderRadius: 2,
                                width: '80%',
                                bgcolor: 'lexisColorLogo',
                            }}>
                            {isEFP ? 'Website' : 'LEXIS Website'}
                        </Button>
                        <Button
                            component='a'
                            href={documentationUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            variant='contained'
                            color='secondary'
                            sx={{
                                py: { xs: 1.2, sm: 1.5 },
                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                borderRadius: 2,
                                width: '80%',
                            }}>
                            Documentation
                        </Button>
                    </Stack>
                </Container>

                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 0,
                        width: '80%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        pb: { xs: 1, sm: 2 },
                    }}>
                    <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{
                            fontSize: { xs: '0.6rem', sm: '0.75rem' },
                            textAlign: 'center',
                            bottom: { xs: 20, sm: 40 },
                        }}>
                        Copyright © {new Date().getFullYear()}
                        {!isExtraSmall && ' IT4Innovations National Supercomputing Center'}
                        {isExtraSmall && ' IT4Innovations'}. All rights reserved.
                    </Typography>

                    <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{
                            fontSize: { xs: '0.5rem', sm: '0.65rem' },
                            alignSelf: 'flex-end',
                        }}>
                        <FormattedMessage id='version' />-{GIT_COMMIT_HASH} -{GIT_BRANCH_NAME}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};

export default LoginPage;
